/* File: CalendarSettings.jsx
 * Path: ananunadm/src/components/calendar/CalendarSettings.jsx
 * Description: Per-user Google account connection. OAuth app credentials are
 * configured only on the APIT backend and are never displayed here.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
	Badge,
	Button,
	Card,
	Group,
	Loader,
	Stack,
	Text,
	Title
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
	IconBrandGoogle,
	IconCheck,
	IconTrash
} from "@tabler/icons-react";
import axiosApi from "@/helpers/api";
import config from "@/config";

const route = config.api.calendarIntegrations || "calendar-integrations";

export default function CalendarSettings({
	embedded = false,
	onChanged = () => {},
	status: controlledStatus,
	loading: controlledLoading = false
}) {
	const controlled = controlledStatus !== undefined;
	const [localStatus, setLocalStatus] = useState(null);
	const [loading, setLoading] = useState(!controlled);
	const [busyAction, setBusyAction] = useState("");
	const status = controlled ? controlledStatus : localStatus;

	const loadStatus = useCallback(async () => {
		if (controlled) return;
		setLoading(true);
		try {
			const response = await axiosApi.get(`${route}/google/status`);
			setLocalStatus(response.data || null);
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || "Could not load Google integration"
			});
		} finally {
			setLoading(false);
		}
	}, [controlled]);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	useEffect(() => {
		function onMessage(event) {
			if (event?.data?.source !== "apit-calendar-integration") return;
			if (event.data.status === "connected") {
				notifications.show({
					color: "green",
					message: "Google account connected",
					icon: <IconCheck size={16} />
				});
			} else {
				notifications.show({
					color: "red",
					message: event.data.message || "Google connection failed"
				});
			}
			loadStatus();
			onChanged();
		}
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [loadStatus, onChanged]);

	async function connectGoogle() {
		setBusyAction("connect");
		try {
			const response = await axiosApi.get(`${route}/google/auth-url`);
			const authUrl = response.data?.url;
			if (!authUrl) throw new Error("Google auth URL was not returned");
			const popup = window.open(
				authUrl,
				"apit-google-connect",
				"width=560,height=720,menubar=no,toolbar=no,location=yes,status=no"
			);
			if (!popup) window.location.href = authUrl;
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || error.message || "Could not start Google connection"
			});
		} finally {
			setBusyAction("");
		}
	}

	async function disconnectGoogle() {
		setBusyAction("disconnect");
		try {
			await axiosApi.post(`${route}/google/disconnect`);
			await loadStatus();
			onChanged();
			notifications.show({
				color: "green",
				message: "Google account disconnected",
				icon: <IconCheck size={16} />
			});
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || "Could not disconnect Google account"
			});
		} finally {
			setBusyAction("");
		}
	}

	if (loading || controlledLoading) {
		return (
			<Group position="center" p="xl">
				<Loader />
			</Group>
		);
	}

	const connected = Boolean(status?.connected);
	const needsReconsent = connected && Boolean(status?.needsReconsent);

	return (
		<Stack spacing="lg" p={embedded ? 0 : "md"}>
			{!embedded && <Title order={2}>Google Workspace Integration</Title>}
			<Card withBorder radius="md" p="md">
				<Group position="apart" align="center">
					<Group spacing="sm">
						<IconBrandGoogle size={24} />
						<div>
							<Text weight={700}>Google account</Text>
							<Text size="sm" color="dimmed">
								{connected
									? `Connected as: ${status.googleEmail || status.providerUserEmail || "Google user"}`
									: "Connect your own Google account to APIT."}
							</Text>
							{needsReconsent && (
								<Text size="xs" color="orange">
									Reconnect to grant the required access for enabled Google services
									{status?.missingScopeServices?.length
										? ` (${status.missingScopeServices.join(", ")})`
										: ""}.
								</Text>
							)}
						</div>
					</Group>
					<Group spacing="sm">
						<Badge color={needsReconsent ? "orange" : connected ? "green" : "gray"} variant="light">
							{needsReconsent ? "Needs reconnect" : connected ? "Connected" : "Not connected"}
						</Badge>
						{needsReconsent && (
							<Button
								variant="light"
								color="orange"
								leftIcon={<IconBrandGoogle size={16} />}
								onClick={connectGoogle}
								loading={busyAction === "connect"}
							>
								Reconnect
							</Button>
						)}
						{connected ? (
							<Button
								variant="light"
								color="red"
								leftIcon={<IconTrash size={16} />}
								onClick={disconnectGoogle}
								loading={busyAction === "disconnect"}
							>
								Disconnect
							</Button>
						) : (
							<Button
								leftIcon={<IconBrandGoogle size={16} />}
								onClick={connectGoogle}
								loading={busyAction === "connect"}
							>
								Connect Google Account
							</Button>
						)}
					</Group>
				</Group>
			</Card>
		</Stack>
	);
}
